import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { MessageCircle, Users, Moon, ArrowRight, Check, User, Phone } from 'lucide-react';
import { whatsappNumber } from '../data/mockData';
import { useLanguage } from '../contexts/LanguageContext';
import { useAvailability } from '../contexts/AvailabilityContext';
import { DatePickerCalendar } from './DatePickerCalendar';
import { format, addDays, differenceInDays } from 'date-fns';


export const BookingForm = () => {
  const { t } = useLanguage();
  const { isRangeOverlapping } = useAvailability();
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState(2);
  const [guestName, setGuestName] = useState('');
  const [guestContact, setGuestContact] = useState('');
  const [message, setMessage] = useState('');
  const [overlap, setOverlap] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setOverlap(isRangeOverlapping(checkIn, checkOut));
  }, [checkIn, checkOut, isRangeOverlapping]);

  useEffect(() => {
    if (checkIn && checkOut) {
      const minOut = format(addDays(new Date(checkIn), 5), 'yyyy-MM-dd');
      if (checkOut < minOut) setCheckOut('');
    }
  }, [checkIn, checkOut]);

  const nights = checkIn && checkOut
    ? Math.max(0, differenceInDays(new Date(checkOut), new Date(checkIn)))
    : 0;

  const minCheckOut = checkIn
    ? format(addDays(new Date(checkIn), 5), 'yyyy-MM-dd')
    : '';

  const todayStr = format(new Date(), 'yyyy-MM-dd');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!checkIn || !checkOut || overlap || !guestName.trim() || !guestContact.trim()) return;

    const bf = t('bookingForm');
    const checkInFormatted = format(new Date(checkIn), 'dd.MM.yyyy');
    const checkOutFormatted = format(new Date(checkOut), 'dd.MM.yyyy');

    // Send booking to backend for email notification
    setSending(true);
    try {
      await fetch('/api/booking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guest_name: guestName.trim(),
          guest_contact: guestContact.trim(),
          check_in: checkInFormatted,
          check_out: checkOutFormatted,
          nights,
          guests,
          message: message.trim(),
        }),
      });
    } catch (err) {
      // Don't block WhatsApp if email fails
      console.error('Booking notification error:', err);
    }
    setSending(false);

    // GA4 events
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'booking_request', {
        check_in: checkInFormatted,
        check_out: checkOutFormatted,
        nights: nights,
        guests: guests,
      });
      window.gtag('event', 'whatsapp_click', {
        check_in: checkInFormatted,
        check_out: checkOutFormatted,
        nights: nights,
        guests: guests,
      });
    }

    let whatsappMsg = bf.whatsappTemplate
      .replace('{checkIn}', checkInFormatted)
      .replace('{checkOut}', checkOutFormatted)
      .replace('{nights}', nights)
      .replace('{guests}', guests)
      .replace('{name}', guestName.trim())
      .replace('{contact}', guestContact.trim());

    if (message.trim()) {
      whatsappMsg += `\n\n${bf.additionalNote}: ${message.trim()}`;
    }

    if (window.gtag) { window.gtag('event', 'whatsapp_click', { event_category: 'engagement', event_label: 'booking_form' }); }
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'Lead', { content_name: 'booking_form', num_nights: nights, num_guests: guests });
    }
    window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMsg)}`, '_blank');
  };

  const bf = t('bookingForm');
  const canSubmit = checkIn && checkOut && !overlap && nights >= 5 && guestName.trim() && guestContact.trim() && !sending;

  return (
    <section className="booking-form-section" id="booking-form">
      <div className="container-custom">
        <div className="text-center mb-12">
          <div className="section-badge">{bf.badge}</div>
          <h2 className="section-title">{bf.title}</h2>
          <p className="section-description">{bf.description}</p>
        </div>

        <Card className="booking-form-card">
          <CardContent className="booking-form-content">
            <form onSubmit={handleSubmit} className="booking-form" data-testid="booking-form">
              <div className="booking-form-grid">
                <div className="form-group">
                  <label htmlFor="guestName">
                    <User className="h-4 w-4" />
                    {bf.guestName}
                  </label>
                  <input
                    type="text"
                    id="guestName"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    placeholder={bf.guestNamePlaceholder}
                    required
                    data-testid="guest-name-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="guestContact">
                    <Phone className="h-4 w-4" />
                    {bf.guestContact}
                  </label>
                  <input
                    type="text"
                    id="guestContact"
                    value={guestContact}
                    onChange={(e) => setGuestContact(e.target.value)}
                    placeholder={bf.guestContactPlaceholder}
                    required
                    data-testid="guest-contact-input"
                  />
                </div>

                <div className="form-group">
                  <label>
                    {bf.checkIn}
                  </label>
                  <DatePickerCalendar
                    value={checkIn}
                    onChange={setCheckIn}
                    minDate={todayStr}
                    label={bf.selectDate || 'Select date'}
                    testId="checkin-input"
                    mode="checkin"
                  />
                </div>

                <div className="form-group">
                  <label>
                    {bf.checkOut}
                  </label>
                  <DatePickerCalendar
                    value={checkOut}
                    onChange={setCheckOut}
                    minDate={minCheckOut || todayStr}
                    label={bf.selectDate || 'Select date'}
                    testId="checkout-input"
                    mode="checkout"
                    checkInDate={checkIn}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="guests">
                    <Users className="h-4 w-4" />
                    {bf.guests}
                  </label>
                  <select
                    id="guests"
                    value={guests}
                    onChange={(e) => setGuests(Number(e.target.value))}
                    data-testid="guests-select"
                  >
                    {[1,2,3,4,5,6,7,8].map(n => (
                      <option key={n} value={n}>{n} {n === 1 ? bf.guest : bf.guestsLabel}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">
                    <MessageCircle className="h-4 w-4" />
                    {bf.specialRequests}
                  </label>
                  <input
                    type="text"
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={bf.placeholder}
                    data-testid="message-input"
                  />
                </div>
              </div>

              {overlap && (
                <div className="booking-overlap-warning" data-testid="overlap-warning">
                  {bf.overlapWarning}
                </div>
              )}

              {nights > 0 && !overlap && (
                <div className="booking-summary" data-testid="booking-summary">
                  <div className="summary-item">
                    <Moon className="h-4 w-4" />
                    <span>{nights} {bf.nightsLabel}</span>
                  </div>
                  <div className="summary-item">
                    <Users className="h-4 w-4" />
                    <span>{guests} {guests === 1 ? bf.guest : bf.guestsLabel}</span>
                  </div>
                  <div className="summary-item">
                    <Check className="h-4 w-4" />
                    <span>{bf.datesAvailable}</span>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                className="booking-submit-btn"
                size="lg"
                disabled={!canSubmit}
                data-testid="booking-submit"
              >
                {sending ? (
                  <span>{bf.sendingLabel || 'Sending...'}</span>
                ) : (
                  <>
                    <MessageCircle className="mr-2 h-5 w-5" />
                    {bf.submitButton}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>

              {nights > 0 && nights < 5 && (
                <p className="min-stay-note">{bf.minStayNote}</p>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};
